import { Modal, Typography, Divider, Table, Tag, Space } from 'antd';

const { Title, Paragraph, Text } = Typography;

// ============================================================
// 规则引擎「新建规则」使用说明（书本图标入口，2026-10-04 用户要求）
// - 一、名词解释（重要名称）
// - 二、用例说明（设备离线告警 / 检测超阈值告警 / IO 联动控制）
// - 三、试跑与生效
// ============================================================

export default function RuleHelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onCancel={onClose} footer={null} width={780} title="新建规则 · 使用说明" data-testid="rule-help-modal">
      <Typography style={{ maxHeight: '68vh', overflowY: 'auto', paddingRight: 8 }}>
        <Title level={5} style={{ marginTop: 0 }}>一、名词解释</Title>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong>规则</Text>：一条"当满足什么条件 → 执行什么动作"的自动化策略；保存并启用后在设备事件到达时自动评估执行。
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong>事件</Text>：设备上报的业务事件（如 AI 检测 AI_DETECTION、IO 触发 IO_TRIGGER、升级完成 UPGRADE_OK），来自 MQTT 上行 rtu/&#123;imei&#125;/up/event。
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong>事件码快筛</Text>：只把列出的设备事件送入本规则评估（留空 = 全部事件）。仅用于性能过滤，<Text type="secondary">不会替代"条件"的命中判断</Text>。示例：AI_DETECTION。
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong>条件（facts）</Text>：从事件消息中取字段做判断。可引用顶层字段与点路径：eventCode / severity / module / imei / deviceId / detail.count / detail.di / detail.classes 等。多条条件可选择"全部满足(AND)"或"任一满足(OR)"。
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong>算子</Text>：比较方式——等于/不等于/大于/大于等于/小于/小于等于/在列表中(in)/数组包含/字符串包含/正则匹配。值输入会自动识别数字与 true/false；in 用英文逗号分隔多个值。
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong>动作</Text>：命中后执行的操作，可组合多个——<Text strong>告警</Text>（生成平台告警 + 实时推送）、<Text strong>命令</Text>（向设备下发契约命令，如 write_do 闭合继电器）、<Text strong>Webhook</Text>（调用三方系统）、<Text strong>广播</Text>（推送自定义 WS 事件给大屏/应用）。
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text strong>冷却（ms）</Text>：同一条规则在该时间窗口内最多触发一次，防止抖动刷屏。示例：60000 = 1 分钟最多一次；0 = 不限制。
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>优先级</Text>：数字越小越先评估；多条规则可同时命中并各自执行。建议重要告警用小值（如 10），常规用 100。
        </Paragraph>

        <Divider style={{ margin: '14px 0' }} />
        <Title level={5}>二、用例说明（点表单里的「规则向导」标签可一键生成，再微调）</Title>

        <Paragraph style={{ marginBottom: 6 }}>
          <Text strong>用例 1：设备离线告警</Text>
          <Space size={6} style={{ marginLeft: 8 }}><Tag color="blue">向导：设备离线告警</Tag></Space>
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          场景：关注设备断网/断电。步骤：点向导 → 保持条件 <Text code>eventCode == DEVICE_OFFLINE</Text>（如网关侧事件码不同，改成实际离线事件码）→ 动作「告警」level=error、文案"设备离线，请检查网络/供电" → 试跑验证 → 保存启用。
        </Paragraph>

        <Paragraph style={{ marginBottom: 6 }}>
          <Text strong>用例 2：检测超阈值告警</Text>
          <Space size={6} style={{ marginLeft: 8 }}><Tag color="blue">向导：检测超阈值告警</Tag></Space>
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          场景：AI 相机识别人数/目标数超限时告警。步骤：点向导 → 条件为 <Text code>eventCode == AI_DETECTION</Text> 且 <Text code>detail.count &gt;= 3</Text>（把 3 改成你的阈值）→ 动作「告警」level=warn → 用试跑的「AI 检测（人数≥1）」样例事件验证命中 → 保存。
          <br />进阶：只想统计"person"类别时，条件改用算子「数组包含」：<Text code>detail.classes 数组包含 person</Text>。
        </Paragraph>

        <Paragraph style={{ marginBottom: 6 }}>
          <Text strong>用例 3：IO 联动控制</Text>
          <Space size={6} style={{ marginLeft: 8 }}><Tag color="blue">向导：IO 联动控制</Tag></Space>
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          场景：DI 触发（如门磁/液位）后自动闭合继电器。步骤：点向导 → 条件 <Text code>eventCode == IO_TRIGGER</Text> 且 <Text code>detail.di == di1</Text> → 动作「命令」write_do，参数 <Text code>{'{"channel":"rl1","value":1}'}</Text> → 试跑（用「IO 触发」样例事件）→ 保存。命令经 MQTT 下发到设备（离线时由边缘规则兜底，需设备支持对应契约命令）。
        </Paragraph>

        <Divider style={{ margin: '14px 0' }} />
        <Title level={5}>三、试跑与生效</Title>
        <Paragraph style={{ marginBottom: 6 }}>
          列表右上角「试跑」：粘贴/选择一条样例事件，按当前租户规则 + 全局规则评估，展示"命中哪些规则、会执行哪些动作"（dry-run，不真正执行、不打扰设备）。
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          建议流程：向导生成 → 试跑命中 → 保存并启用。规则仅对"之后到达"的事件生效；执行情况可在「执行记录」Tab 查看（含动作耗时与错误）。
        </Paragraph>

        <Table
          size="small"
          style={{ marginTop: 12 }}
          pagination={false}
          rowKey="k"
          columns={[
            { title: '动作', dataIndex: 'a', width: 90 },
            { title: '作用', dataIndex: 'b' },
            { title: '示例', dataIndex: 'c' },
          ]}
          dataSource={[
            { k: 'alert', a: '告警', b: '生成设备告警（平台日志 + 大屏/应用实时推送）', c: 'level=warn, message=检测人数超过阈值' },
            { k: 'command', a: '命令', b: '向命中设备下发契约命令（MQTT down/cmd）', c: 'write_do {"channel":"rl1","value":1}' },
            { k: 'webhook', a: 'Webhook', b: '调用三方系统（钉钉/企微/业务系统）', c: 'https://…，超时 5000ms' },
            { k: 'broadcast', a: '广播', b: '推送自定义事件给订阅的页面/大屏', c: 'event=rule:triggered' },
          ]}
        />
      </Typography>
    </Modal>
  );
}
